import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import { formatThaiShortDateTwoDigitYear, formatTime } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import { useMutateCostTransactionDelete } from 'modules/cost/api/mutation'
import { useGetTransactionGroup } from 'modules/cost/api/queries'
import { CostTransactionGroupItem } from 'modules/cost/api/types'
import { useSpeciesName } from 'modules/pond/api/selector'
import MoreAction from 'modules/pond/components/MoreAction'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import EditIcon from '@mui/icons-material/Edit'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Box, Card, Collapse, Skeleton, Stack, Typography, useTheme } from '@mui/material'

import CostDialogCreateUpdate from '../CostDialogCreateUpdate'
import TransactionTypeChip from '../TransactionTypeChip'
import EditPricePerUnitDialog from './EditPricePerUnitDialog'
import LotTag from './LotTag'
import { normalizeSpeciesName } from 'utils/analytics'

const CostGroupSection = () => {
  const { t } = useTranslation('cost')
  const theme = useTheme()
  const { data: groups, isLoading } = useGetTransactionGroup()

  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [editPriceDialog, setEditPriceDialog] = useState<{
    open: boolean
    item: CostTransactionGroupItem | null
  }>({ open: false, item: null })
  const [editTransactionOpen, setEditTransactionOpen] = useState(false)
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null)

  const { mutateAsync: deleteTransaction } = useMutateCostTransactionDelete()
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()
  const { dialog: deleteDialog, openDialog: openDeleteDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      if (selectedTransactionId) {
        await deleteTransaction(selectedTransactionId, {
          onSuccess: () => {
            const normalizedSpecies = normalizeSpeciesName(speciesName)
            trackEvent(
              AnalyticsEvents.costRecordDeleted,
              normalizedSpecies ? { species: normalizedSpecies } : undefined
            )
          },
        })
      }
    },
  })

  const toggleGroup = (groupId: string) => {
    setExpanded((prev) => ({ ...prev, [groupId]: !prev[groupId] }))
  }

  const renderAction = (item: CostTransactionGroupItem) => {
    if (item.transaction_type === 'note') {
      return (
        <MoreAction
          featureKey={FeatureKey.Cost}
          isHideOnCompleted={false}
          onClickEdit={() => {
            setSelectedTransactionId(item.transaction_id ?? null)
            setEditTransactionOpen(true)
          }}
          onClickDelete={() => {
            setSelectedTransactionId(item.transaction_id ?? null)
            openDeleteDialog()
          }}
        />
      )
    }

    if (item.transaction_type === 'use_product') {
      return (
        <MoreAction
          featureKey={FeatureKey.Cost}
          isHideOnCompleted={false}
          onClickOptions={[
            {
              icon: <EditIcon />,
              text: t('edit.price.per.unit.action'),
              action: () => setEditPriceDialog({ open: true, item }),
              crudIdx: CRUDAction.Update,
            },
          ]}
        />
      )
    }

    return <div style={{ width: '40px' }} />
  }

  return (
    <Card
      sx={{
        p: 2,
        mt: 4,
        boxShadow: 'none',
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 2,
      }}
    >
      <Typography fontSize={16} fontWeight={600} mb={2}>
        {t('group.section.title')}
      </Typography>

      {isLoading ? (
        <Stack spacing={1}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={52} sx={{ borderRadius: 1 }} />
          ))}
        </Stack>
      ) : !groups || groups.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" py={3}>
          {t('chart.placeholder')}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {groups.map((group) => (
            <Box key={group.id}>
              {/* Group header */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  bgcolor: theme.palette.grey[200],
                  borderRadius: expanded[group.id] ? '8px 8px 0 0' : 1,
                }}
                onClick={() => toggleGroup(group.id)}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography fontWeight={600} fontSize={15}>
                    {group.name}
                  </Typography>
                  <TransactionTypeChip transactionType={group.transaction_type} />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography fontWeight={600} fontSize={16} color="primary.main">
                    ฿{fNumber(group.total_cost)}
                  </Typography>
                  {expanded[group.id] ? (
                    <ExpandLessIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  )}
                </Stack>
              </Stack>

              {/* Expanded items */}
              <Collapse in={!!expanded[group.id]}>
                <Stack spacing={1} sx={{ bgcolor: theme.palette.grey[200], p: 1 }}>
                  {group.items.map((item, itemIndex) => (
                    <Box
                      key={`${item.stock_id}-${itemIndex}`}
                      sx={{ bgcolor: theme.palette.background.paper, borderRadius: 1, p: 1 }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        {/* Left: name + type chip + lot */}
                        <Box>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography fontWeight={600} fontSize={14}>
                              {item.name}
                              {item.stock_deleted_at ? ` ${t('deleted_suffix')}` : ''}
                            </Typography>
                            <TransactionTypeChip transactionType={item.transaction_type} />
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={0.75} mt={0.5}>
                            {item.transaction_type === 'use_product' ||
                            item.transaction_type === 'product_issue' ? (
                              <>
                                <LotTag />
                                <Typography fontSize={12} color="text.secondary">
                                  {item.lot || '-'}
                                </Typography>
                              </>
                            ) : (
                              <Typography fontSize={12} color="text.secondary">
                                {item.date_time ? (
                                  <>
                                    {formatThaiShortDateTwoDigitYear(item.date_time)}{' '}
                                    {formatTime(item.date_time)}
                                  </>
                                ) : (
                                  '-'
                                )}
                              </Typography>
                            )}
                          </Stack>
                        </Box>

                        {/* Right: qty x price | total | action */}
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography fontSize={12} color="text.secondary" whiteSpace="nowrap">
                            {fNumber(item.quantity)} {item.unit} x ฿{fNumber(item.price_per_unit)}
                          </Typography>
                          <Typography
                            fontWeight={600}
                            fontSize={14}
                            whiteSpace="nowrap"
                            sx={{
                              minWidth: 70,
                              textAlign: 'right',
                              color:
                                item.transaction_type === 'use_product' && item.total_amount === 0
                                  ? 'warning.main'
                                  : 'text.primary',
                            }}
                          >
                            ฿{fNumber(item.total_amount)}
                          </Typography>
                          {renderAction(item)}
                        </Stack>
                      </Stack>
                      {item.transaction_type === 'use_product' && item.total_amount === 0 && (
                        <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'warning.dark' }} />
                          <Typography fontSize={12} color="warning.dark">
                            {t('group.item.zero.cost.prefix')}
                          </Typography>
                          <MoreVertIcon sx={{ fontSize: 14, color: 'warning.dark' }} />
                          <Typography fontSize={12} color="warning.dark">
                            {t('group.item.zero.cost.suffix')}
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Collapse>
            </Box>
          ))}
        </Stack>
      )}

      {editPriceDialog.item && (
        <EditPricePerUnitDialog
          open={editPriceDialog.open}
          onClose={() => setEditPriceDialog({ open: false, item: null })}
          stockId={editPriceDialog.item.stock_id}
          currentPricePerUnit={editPriceDialog.item.price_per_unit}
        />
      )}

      {selectedTransactionId && (
        <CostDialogCreateUpdate
          open={editTransactionOpen}
          onClose={() => {
            setEditTransactionOpen(false)
            setSelectedTransactionId(null)
          }}
          transactionId={selectedTransactionId}
          isEdit={true}
        />
      )}

      {deleteDialog}
    </Card>
  )
}

export default CostGroupSection
